/**
 * Frontend-Service: Premium-Support-Chat REST-Wrapper
 *
 * Backend-Endpoints (siehe backend/src/routes/support/chat.ts):
 *   GET  /support/conversations               -- alle aktiven Konversationen des Users
 *   GET  /support/conversations/active        -- aktive Konversation, anlegen falls keine
 *   GET  /support/conversations/:id/messages  -- Nachrichten einer Konversation
 *   POST /support/conversations/:id/messages  -- Nachricht senden
 *   POST /support/conversations/:id/read      -- als gelesen markieren
 */

import apiClient from '../api/apiClient';

export type ConversationStatus = 'open' | 'in_progress' | 'waiting_user' | 'closed' | 'archived';
export type ConversationCategory = 'question' | 'bug' | 'feature' | 'account' | 'other';
export type MessageSenderType = 'user' | 'admin' | 'system';
export type MessageContentType = 'text' | 'image' | 'system';

export interface ConversationSummary {
  id: string;
  status: ConversationStatus;
  category: ConversationCategory | null;
  subject: string | null;
  unreadCountUser: number;
  unreadCountAdmin: number;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastMessageSenderType: MessageSenderType | null;
  isReadOnly: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChatAttachment {
  id: string;
  /** Relativer URL-Pfad — Frontend baut Base-URL via getImageUrl() drum */
  storagePath: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  width: number | null;
  height: number | null;
  /** Optionaler Bildtitel, vom User gepflegt. null = kein Titel. */
  caption: string | null;
}

/** Snippet einer zitierten Nachricht — wird beim Listing mitgeladen. */
export interface ChatReplySnippet {
  id: string;
  senderType: MessageSenderType;
  /** Text-Vorschau (max 80 Zeichen). Leer bei reinen Bild-Nachrichten. */
  contentPreview: string;
  /** True wenn die zitierte Nachricht Anhänge hatte. */
  hasAttachments: boolean;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderType: MessageSenderType;
  senderId: string | null;
  content: string;
  contentType: MessageContentType;
  isInternalNote: boolean;
  /** Empfänger-Browser hat das Frame empfangen (3-Stufen-Häkchen) */
  deliveredAt: string | null;
  readAt: string | null;
  editedAt: string | null;
  createdAt: string;
  /** Bild-Anhänge (max 3 lt. Anforderung). Leeres Array bei reinen Text-Nachrichten. */
  attachments: ChatAttachment[];
  /** Optionale Antwort-Referenz auf eine andere Nachricht der gleichen Konversation. */
  replyTo: ChatReplySnippet | null;
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
}

export async function fetchActiveConversation(): Promise<ConversationSummary> {
  const res = await apiClient.get<SuccessEnvelope<{ conversation: ConversationSummary }>>(
    '/support/conversations/active'
  );
  return res.data.data.conversation;
}

export async function fetchMessages(
  conversationId: string,
  options: { before?: string; limit?: number } = {}
): Promise<{ messages: ChatMessage[]; conversation: ConversationSummary }> {
  const params = new URLSearchParams();
  if (options.before) params.set('before', options.before);
  if (options.limit) params.set('limit', String(options.limit));
  const qs = params.toString();
  const url = `/support/conversations/${conversationId}/messages${qs ? `?${qs}` : ''}`;

  const res = await apiClient.get<SuccessEnvelope<{ messages: ChatMessage[]; conversation: ConversationSummary }>>(url);
  return res.data.data;
}

export async function sendMessage(
  conversationId: string,
  content: string,
  replyToMessageId?: string | null
): Promise<ChatMessage> {
  const res = await apiClient.post<SuccessEnvelope<{ message: ChatMessage }>>(
    `/support/conversations/${conversationId}/messages`,
    { content, ...(replyToMessageId ? { replyToMessageId } : {}) }
  );
  return res.data.data.message;
}

/**
 * Sendet eine Nachricht mit bis zu 3 Bild-Anhängen via multipart/form-data.
 * Server-seitig wird jedes Bild zu WebP konvertiert + auf max 2048px gerescaled
 * + EXIF-strippen. Text ist optional (reine Bild-Messages erlaubt).
 *
 * Frontend-Komprimierung passiert vor dem Aufruf — wir senden hier nur die
 * (bereits komprimierten) File-Objekte hoch.
 */
export async function sendMessageWithImages(
  conversationId: string,
  text: string,
  images: File[],
  captions: (string | null)[] = [],
  replyToMessageId?: string | null
): Promise<ChatMessage> {
  if (images.length === 0 && text.trim().length === 0) {
    throw new Error('Bitte Text oder mindestens ein Bild hinzufügen.');
  }
  if (images.length > 3) {
    throw new Error('Maximal 3 Bilder pro Nachricht.');
  }

  const formData = new FormData();
  formData.append('text', text);
  if (replyToMessageId) {
    formData.append('replyToMessageId', replyToMessageId);
  }
  for (let i = 0; i < images.length; i++) {
    formData.append('images', images[i]!, images[i]!.name);
    // Jeder 'captions'-Slot wird mit gleichem Index zur Datei geliefert.
    // Server macht dasselbe Indexing.
    formData.append('captions', captions[i] ?? '');
  }

  // WICHTIG: Content-Type muss explizit auf undefined, sonst zwingt der
  // apiClient-Default ('application/json') Axios in den JSON-Serializer und
  // FormData wird zu '{}' verstümmelt. Mit undefined erkennt Axios den
  // FormData-Body und setzt korrekt 'multipart/form-data; boundary=...'.
  const res = await apiClient.post<SuccessEnvelope<{ message: ChatMessage }>>(
    `/support/conversations/${conversationId}/messages/with-images`,
    formData,
    { headers: { 'Content-Type': undefined } as any }
  );
  return res.data.data.message;
}

export async function markConversationRead(conversationId: string): Promise<void> {
  await apiClient.post(`/support/conversations/${conversationId}/read`, {});
}
